import React, { useState, useRef, useEffect } from "react"
import styled from "styled-components"
import { MdKeyboardArrowDown } from "react-icons/md"
import { humanReadableSize } from "../../Computation/utilFunctions"

/*
 * Reusable file picker dropdown.
 *
 * Each file is { id, name, start?, end?, size? }. The secondary line ("Aug 18
 * 2025, 4:11 PM · 17h 23m · 1.38 GB") is built from whatever metadata is present.
 *
 * Props:
 *   files       — array of file objects
 *   value       — selected file id
 *   onChange    — (id) => void
 *   placeholder — text shown when nothing is selected
 *   statusFor   — OPTIONAL (file) => ReactNode. When provided, the returned node
 *                 renders on the right of each row (e.g. a "Loaded / Not loaded"
 *                 pill for the Select-a-Patient-File use case). Omit it and no
 *                 status column is shown (the export use case).
 */
export function FileSelectDropdown({ files = [], value, onChange, placeholder = "Select a file", statusFor }) {
    const [open, setOpen] = useState(false)
    const ref = useRef(null)

    // Close on outside click.
    useEffect(() => {
        if (!open) return undefined
        const onPointerDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener("mousedown", onPointerDown)
        return () => document.removeEventListener("mousedown", onPointerDown)
    }, [open])

    const selected = files.find(f => f.id === value) || null

    return (
        <FileSelect ref={ref}>
            <FileSelectButton
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen(o => !o)}
            >
                <FileMeta>
                    <FileNameRow>
                        <FilePath title={selected?.name}>{selected?.name || placeholder}</FilePath>
                    </FileNameRow>
                    {selected && <FileMetaLine file={selected} />}
                </FileMeta>
                {selected && statusFor && <StatusSlot>{statusFor(selected)}</StatusSlot>}
                <Chevron $open={open}><MdKeyboardArrowDown size={20} /></Chevron>
            </FileSelectButton>

            {open && (
                <FileOptions role="listbox">
                    {files.map(f => (
                        <FileOption
                            key={f.id}
                            role="option"
                            aria-selected={value === f.id}
                            $selected={value === f.id}
                            onClick={() => { onChange(f.id); setOpen(false) }}
                        >
                            <FileMeta>
                                <FileNameRow>
                                    <FilePath title={f.name}>{f.name}</FilePath>
                                </FileNameRow>
                                <FileMetaLine file={f} />
                            </FileMeta>
                            {statusFor && <StatusSlot>{statusFor(f)}</StatusSlot>}
                        </FileOption>
                    ))}
                </FileOptions>
            )}
        </FileSelect>
    )
}

export default FileSelectDropdown

// ---- metadata helpers ----

function formatDuration(ms) {
    if (!ms || ms < 0) return ""
    const mins = Math.floor(ms / 60000)
    const h = Math.floor(mins / 60)
    const m = mins % 60
    return h > 0 ? `${h}h ${m}m` : `${m}m`
}

// The "when" part of a file's metadata: "Aug 18 2025, 4:11 PM · 17h 23m".
function formatFileWhen(file) {
    const parts = []
    if (file?.start) {
        const start = new Date(file.start)
        if (!isNaN(start)) {
            parts.push(start.toLocaleString([], {
                month: "short", day: "numeric", year: "numeric",
                hour: "numeric", minute: "2-digit",
            }))
        }
    }
    if (file?.start && file?.end) {
        const dur = formatDuration(new Date(file.end) - new Date(file.start))
        if (dur) parts.push(dur)
    }
    return parts.join(" · ")
}

function fileSizeStr(file) {
    return Number(file?.size) > 0 ? humanReadableSize(Number(file.size)) : ""
}

// Metadata line under the file name: muted date/duration, then the size (blue,
// extra left margin) beside it.
function FileMetaLine({ file }) {
    const when = formatFileWhen(file)
    const sizeStr = fileSizeStr(file)
    if (!when && !sizeStr) return null
    return (
        <FileExtra>
            {when && <span>{when}</span>}
            {sizeStr && <FileSize>{sizeStr}</FileSize>}
        </FileExtra>
    )
}

// ---- styled ----

const FileSelect = styled.div`
    position: relative;
    width: 100%;
`

const FileSelectButton = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
    border: 1.5px solid var(--color-accent-interactive);
    border-radius: 6px;
    background: var(--color-surface-primary);
    color: var(--color-text-primary);
    cursor: pointer;
    text-align: left;
    &:hover { background: var(--color-hover-neutral); }
`

const FileOptions = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 20;
    background: var(--color-surface-primary);
    border: 1px solid var(--color-border-default);
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
    max-height: 260px;
    overflow-y: auto;
`

const FileOption = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
    cursor: pointer;
    background: ${p => (p.$selected ? "var(--color-hover-neutral)" : "transparent")};
    &:hover { background: var(--color-hover-neutral); }
    & + & { border-top: 1px solid var(--color-border-default); }
`

const FileMeta = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
`

const FileNameRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
`

const FilePath = styled.span`
    flex: 1;
    min-width: 0;
    font-family: 'Source Sans Pro';
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const FileExtra = styled.div`
    display: flex;
    align-items: baseline;
    font-family: 'Source Sans Pro';
    color: var(--text-secondary-color);
    font-size: 12px;
    margin-top: 2px;
`

const FileSize = styled.span`
    flex-shrink: 0;
    margin-left: 14px;
    font-family: 'Source Sans Pro';
    font-size: 13px;
    font-weight: 600;
    color: var(--color-accent-interactive);
    white-space: nowrap;
`

const StatusSlot = styled.div`
    flex-shrink: 0;
    margin-left: 12px;
`

const Chevron = styled.span`
    flex-shrink: 0;
    display: inline-flex;
    color: var(--text-secondary-color);
    transition: transform 150ms ease;
    transform: rotate(${p => (p.$open ? "180deg" : "0deg")});
`
